import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { CytologyFormSchema } from '@/types/formSchema'
import { logger } from '@/utils/logger'

interface CytologyReportData {
  // Patient Info
  patientName: string
  age: number
  gender: string
  patientCode: string
  address: string
  province: string
  department: string
  treatingDoctor: string
  clinicalDiagnosis: string
  specimenType: string

  // Bethesda 2014 Results
  organisms: {
    trichomonas: boolean
    candida: boolean
    bacterialVaginosis: boolean
    actinomyces: boolean
    hsv: boolean
    hpv: boolean
  }

  otherFindings: {
    normalCells: boolean
    inflammation: boolean
    radiation: boolean
    iud: boolean
    atrophy: boolean
    postHysterectomy: boolean
    endometrialCells: boolean
  }

  squamousCells: {
    ascus: boolean
    asch: boolean
    lsil: boolean
    lsilHpv: boolean
    hsil: boolean
    squamousCarcinoma: boolean
  }

  glandularCells: {
    agc: boolean
    ais: boolean
    endocervicalAdenocarcinoma: boolean
    endometrialAdenocarcinoma: boolean
    adenocarcinomaNos: boolean
    adenocarcinomaExtrauterine: boolean
  }

  conclusion: string
  recommendation: string
  cytopathologist: string
  reportDate: Date
}

interface CytologyReportTemplateProps {
  patientId?: string
  onSave?: (data: CytologyReportData) => void
  onCancel?: () => void
}

const CytologyReportTemplate: React.FC<CytologyReportTemplateProps> = ({
  onSave,
  onCancel
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [hospitalName, setHospitalName] = useState('SỞ Y TẾ THÁI NGUYÊN')
  const [headerText, setHeaderText] = useState('KẾT QUẢ XÉT NGHIỆM TẾ BÀO HỌC SÀNG LỌC UNG THƯ CỔ TỬ CUNG')
  const [hospitalAddress, setHospitalAddress] = useState('Thịnh Đán, Thành phố Thái Nguyên, Thái Nguyên')
  const [logoUrl, setLogoUrl] = useState('🏥')

  // Dynamic form state
  const [formSchema, setFormSchema] = useState<CytologyFormSchema | null>(null)
  const [dynamicFormData, setDynamicFormData] = useState<Record<string, any>>({})
  const [dynamicFormErrors, setDynamicFormErrors] = useState<Record<string, string>>({})

  const [formData, setFormData] = useState<CytologyReportData>({
    patientName: '',
    age: 0,
    gender: 'Nữ',
    patientCode: '',
    address: '',
    province: '',
    department: '',
    treatingDoctor: '',
    clinicalDiagnosis: '',
    specimenType: 'Phết nhúng dịch',
    organisms: {
      trichomonas: false,
      candida: false,
      bacterialVaginosis: false,
      actinomyces: false,
      hsv: false,
      hpv: false
    },
    otherFindings: {
      normalCells: true,
      inflammation: false,
      radiation: false,
      iud: false,
      atrophy: false,
      postHysterectomy: false,
      endometrialCells: false
    },
    squamousCells: {
      ascus: false,
      asch: false,
      lsil: false,
      lsilHpv: false,
      hsil: false,
      squamousCarcinoma: false
    },
    glandularCells: {
      agc: false,
      ais: false,
      endocervicalAdenocarcinoma: false,
      endometrialAdenocarcinoma: false,
      adenocarcinomaNos: false,
      adenocarcinomaExtrauterine: false
    },
    conclusion: 'TẾ BÀO BIỂU MÔ CỔ TỬ CUNG - ÂM ĐẠO TRONG GIỚI HẠN BÌNH THƯỜNG (NEGATIVE FOR INTRAEPITHELIAL LESION OR MALIGNANCY)',
    recommendation: 'Kết hợp kết quả xét nghiệm HPV theo dõi theo khuyến cáo.',
    cytopathologist: '',
    reportDate: new Date()
  })

  // Load template config from API (not localStorage anymore)
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await fetch('/api/v1/pathology/cytology-templates/default/')
        if (response.ok) {
          const template = await response.json()
          const config = template.config

          logger.debug('📋 [CytologyReportTemplate] Loaded template config:', config)
          logger.debug('📋 [CytologyReportTemplate] FormSchema exists:', !!config.formSchema)

          setHospitalName(config.hospitalName || 'SỞ Y TẾ THÁI NGUYÊN')
          setHeaderText(config.headerText || 'KẾT QUẢ XÉT NGHIỆM TẾ BÀO HỌC SÀNG LỌC UNG THƯ CỔ TỬ CUNG')
          setHospitalAddress(config.hospitalAddress || 'Thịnh Đán, Thành phố Thái Nguyên, Thái Nguyên')
          setLogoUrl(config.logoUrl || '🏥')

          // Load form schema
          if (config.formSchema) {
            logger.debug('📋 [CytologyReportTemplate] FormSchema sections:', config.formSchema.sections?.length)
            setFormSchema(config.formSchema)
          }
        } else {
          logger.debug('No default template found, using defaults')
        }
      } catch (error) {
        console.error('Error loading template from API:', error)
      }
    }

    fetchTemplate()
  }, [])

  // Handler for dynamic form field changes
  const handleDynamicFieldChange = (fieldId: string, value: any) => {
    setDynamicFormData(prev => ({ ...prev, [fieldId]: value }))
    if (dynamicFormErrors[fieldId]) {
      setDynamicFormErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[fieldId]
        return newErrors
      })
    }
  }

  // Get custom fields for a specific section
  const getCustomFieldsForSection = (sectionId: string) => {
    if (!formSchema) return []

    const section = formSchema.sections.find(s => s.id === sectionId)
    if (!section || !section.enabled) return []

    // List of built-in field IDs to exclude
    const builtInFields = [
      'patientName', 'age', 'gender', 'patientCode', 'address', 'province',
      'department', 'treatingDoctor', 'clinicalDiagnosis', 'specimenType',
      'organisms', 'squamousCells', 'glandularCells', 'otherFindings',
      'conclusion', 'recommendation', 'cytopathologist'
    ]

    return section.fields.filter(field =>
      field.enabled &&
      !builtInFields.includes(field.id) &&
      field.type !== 'checkboxGroup' // Exclude checkboxGroup vì đã render sẵn
    )
  }

  // Render a custom field
  const renderCustomField = (field: any, _sectionId: string, colorClass: string) => {
    const fieldKey = field.id  // Use field.id directly, not prefixed with sectionId
    const value = dynamicFormData[fieldKey] ?? field.defaultValue ?? ''
    const error = dynamicFormErrors[fieldKey]

    switch (field.type) {
      case 'text':
        return (
          <div key={fieldKey} className="mt-2">
            <label className="flex items-center space-x-2">
              <span className="text-sm font-medium">{field.label}:</span>
              <input
                type="text"
                value={value}
                onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
                placeholder={field.placeholder}
                className={`flex-1 px-2 py-1 border rounded text-sm ${error ? 'border-red-500' : 'border-gray-300'}`}
              />
            </label>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        )

      case 'textarea':
        return (
          <div key={fieldKey} className="mt-2">
            <label className="text-sm font-medium">{field.label}:</label>
            <textarea
              value={value}
              onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
              placeholder={field.placeholder}
              rows={2}
              className={`w-full px-2 py-1 border rounded text-sm mt-1 ${error ? 'border-red-500' : 'border-gray-300'}`}
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        )

      case 'number':
        return (
          <div key={fieldKey} className="mt-2">
            <label className="flex items-center space-x-2">
              <span className="text-sm font-medium">{field.label}:</span>
              <input
                type="number"
                value={value}
                onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
                className={`flex-1 px-2 py-1 border rounded text-sm ${error ? 'border-red-500' : 'border-gray-300'}`}
              />
            </label>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        )

      case 'select':
        return (
          <div key={fieldKey} className="mt-2">
            <label className="flex items-center space-x-2">
              <span className="text-sm font-medium">{field.label}:</span>
              <select
                value={value}
                onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
                className={`flex-1 px-2 py-1 border rounded text-sm ${error ? 'border-red-500' : 'border-gray-300'}`}
              >
                <option value="">-- Chọn --</option>
                {field.options?.map((opt: any) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        )

      case 'checkbox':
        return (
          <label key={fieldKey} className="flex items-center space-x-2 cursor-pointer mt-2">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.checked)}
              className={`w-4 h-4 ${colorClass} rounded`}
            />
            <span className="text-sm">{field.label}</span>
          </label>
        )

      case 'date':
        return (
          <div key={fieldKey} className="mt-2">
            <label className="flex items-center space-x-2">
              <span className="text-sm font-medium">{field.label}:</span>
              <input
                type="date"
                value={value}
                onChange={(e) => handleDynamicFieldChange(fieldKey, e.target.value)}
                className={`flex-1 px-2 py-1 border rounded text-sm ${error ? 'border-red-500' : 'border-gray-300'}`}
              />
            </label>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
        )

      default:
        return null
    }
  }

  // Validate dynamic form fields
  const validateDynamicForm = (): boolean => {
    if (!formSchema) return true

    const errors: Record<string, string> = {}

    formSchema.sections
      .filter(section => section.enabled)
      .forEach(section => {
        section.fields
          .filter(field => field.enabled && field.required)
          .forEach(field => {
            const fieldKey = `${section.id}_${field.id}`
            const value = dynamicFormData[fieldKey]

            if (value === undefined || value === null || value === '') {
              errors[fieldKey] = `${field.label} là bắt buộc`
            }
          })
      })

    setDynamicFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = () => {
    if (!formData.patientName || !formData.cytopathologist) {
      toast.error(t('cytology.validationError'))
      return
    }

    // Validate dynamic form
    if (!validateDynamicForm()) {
      toast.error('Vui lòng điền đầy đủ các trường bắt buộc')
      return
    }

    if (onSave) {
      onSave(formData)
      toast.success(t('cytology.saveSuccess'))
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Action Bar */}
      <div className="max-w-5xl mx-auto mb-6 flex justify-between items-center">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-800 flex items-center"
        >
          ← {t('common.back')}
        </button>
        <button
          onClick={() => navigate('/pathology/reports/cytology/new')}
          className="bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 flex items-center space-x-2"
        >
          <span>+</span>
          <span>Create New Report</span>
        </button>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-lg max-w-5xl mx-auto">
        {/* Header */}
        <div className="border-b-2 border-gray-800 pb-4 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                {logoUrl.startsWith('http') ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain rounded-full" />
                ) : (
                  <span className="text-3xl">{logoUrl}</span>
                )}
              </div>
              <div>
                <h2 className="text-lg font-bold text-blue-900">{hospitalName}</h2>
                <p className="text-sm text-gray-600">{t('cytology.address')}: {hospitalAddress}</p>
              </div>
            </div>
          </div>

          <h1 className="text-center text-xl font-bold text-gray-900 mt-4">
            {headerText}
          </h1>
        </div>

      {/* Patient Information */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium mb-1">{t('cytology.patientName')}:</label>
          <input
            type="text"
            value={formData.patientName}
            onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md font-bold text-lg"
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-sm font-medium mb-1">{t('cytology.age')}:</label>
            <input
              type="number"
              value={formData.age}
              onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('cytology.gender')}:</label>
            <input
              type="text"
              value={formData.gender}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('cytology.patientCode')}:</label>
            <input
              type="text"
              value={formData.patientCode}
              onChange={(e) => setFormData({ ...formData, patientCode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('cytology.address')}:</label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t('cytology.province')}:</label>
          <input
            type="text"
            value={formData.province}
            onChange={(e) => setFormData({ ...formData, province: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('cytology.department')}:</label>
          <input
            type="text"
            value={formData.department}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t('cytology.treatingDoctor')}:</label>
          <input
            type="text"
            value={formData.treatingDoctor}
            onChange={(e) => setFormData({ ...formData, treatingDoctor: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('cytology.clinicalDiagnosis')}:</label>
          <input
            type="text"
            value={formData.clinicalDiagnosis}
            onChange={(e) => setFormData({ ...formData, clinicalDiagnosis: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t('cytology.specimenType')}:</label>
          <select
            value={formData.specimenType}
            onChange={(e) => setFormData({ ...formData, specimenType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="Phết nhúng dịch">{t('cytology.liquidBasedCytology')}</option>
            <option value="Phết trực tiếp">{t('cytology.conventionalSmear')}</option>
          </select>
        </div>
      </div>

      {/* Bethesda 2014 Results */}
      <div className="border-t-2 border-gray-300 pt-4 mb-6">
        <h2 className="text-center text-lg font-bold mb-4 text-blue-900">
          {t('cytology.bethesdaTitle')}
        </h2>

        <div className="grid grid-cols-2 gap-6">
          {/* Organisms */}
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-bold text-green-800 mb-3">{t('cytology.organismsTitle')}</h3>
            <div className="space-y-2">
              {Object.entries({
                trichomonas: t('cytology.trichomonas'),
                candida: t('cytology.candida'),
                bacterialVaginosis: t('cytology.bacterialVaginosis'),
                actinomyces: t('cytology.actinomyces'),
                hsv: t('cytology.hsv'),
                hpv: t('cytology.hpv')
              }).map(([key, label]) => (
                <label key={key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.organisms[key as keyof typeof formData.organisms]}
                    onChange={(e) => setFormData({
                      ...formData,
                      organisms: { ...formData.organisms, [key]: e.target.checked }
                    })}
                    className="w-4 h-4 text-green-600 rounded"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Squamous Cells */}
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-bold text-red-800 mb-3">{t('cytology.squamousCellsTitle')}</h3>
            <div className="space-y-2">
              {Object.entries({
                ascus: t('cytology.ascus'),
                asch: t('cytology.asch'),
                lsil: t('cytology.lsil'),
                lsilHpv: t('cytology.lsilHpv'),
                hsil: t('cytology.hsil'),
                squamousCarcinoma: t('cytology.squamousCarcinoma')
              }).map(([key, label]) => (
                <label key={key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.squamousCells[key as keyof typeof formData.squamousCells]}
                    onChange={(e) => setFormData({
                      ...formData,
                      squamousCells: { ...formData.squamousCells, [key]: e.target.checked }
                    })}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Other Findings */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-bold text-blue-800 mb-3">{t('cytology.otherFindingsTitle')}</h3>
            <div className="space-y-2">
              {Object.entries({
                normalCells: t('cytology.normalCells'),
                inflammation: t('cytology.inflammation'),
                radiation: t('cytology.radiation'),
                iud: t('cytology.iud'),
                atrophy: t('cytology.atrophy'),
                postHysterectomy: t('cytology.postHysterectomy'),
                endometrialCells: t('cytology.endometrialCells')
              }).map(([key, label]) => (
                <label key={key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.otherFindings[key as keyof typeof formData.otherFindings]}
                    onChange={(e) => setFormData({
                      ...formData,
                      otherFindings: { ...formData.otherFindings, [key]: e.target.checked }
                    })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Glandular Cells */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-bold text-purple-800 mb-3">{t('cytology.glandularCellsTitle')}</h3>
            <div className="space-y-2">
              {Object.entries({
                agc: t('cytology.agc'),
                ais: t('cytology.ais'),
                endocervicalAdenocarcinoma: t('cytology.endocervicalAdenocarcinoma'),
                endometrialAdenocarcinoma: t('cytology.endometrialAdenocarcinoma'),
                adenocarcinomaNos: t('cytology.adenocarcinomaNos'),
                adenocarcinomaExtrauterine: t('cytology.adenocarcinomaExtrauterine')
              }).map(([key, label]) => (
                <label key={key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.glandularCells[key as keyof typeof formData.glandularCells]}
                    onChange={(e) => setFormData({
                      ...formData,
                      glandularCells: { ...formData.glandularCells, [key]: e.target.checked }
                    })}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}

              {/* Custom fields for Glandular Cells section */}
              {getCustomFieldsForSection('glandular-cells').map(field =>
                renderCustomField(field, 'glandular-cells', 'text-purple-600')
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mb-6">
        <h3 className="font-bold text-lg mb-2">{t('cytology.conclusion')}:</h3>
        <textarea
          value={formData.conclusion}
          onChange={(e) => setFormData({ ...formData, conclusion: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border-2 border-blue-300 rounded-md font-medium"
        />
      </div>

      {/* Recommendation */}
      <div className="mb-6">
        <h3 className="font-bold text-lg mb-2">{t('cytology.recommendation')}:</h3>
        <textarea
          value={formData.recommendation}
          onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />

        {/* Custom fields for Conclusion section */}
        <div className="mt-4 space-y-2">
          {getCustomFieldsForSection('conclusion').map(field =>
            renderCustomField(field, 'conclusion', 'text-blue-600')
          )}
        </div>
      </div>

      {/* Footer - Signatures */}
      <div className="border-t-2 border-gray-300 pt-4 mt-6">
        <div className="grid grid-cols-2 gap-8">
          <div className="text-center">
            <div className="mb-4">
              <div className="inline-block border-2 border-gray-400 p-2">
                <div className="w-32 h-32 bg-gray-100 flex items-center justify-center">
                  <span className="text-4xl">📱</span>
                </div>
                <p className="text-xs mt-2">{t('cytology.scanQR')}</p>
              </div>
            </div>
            <p className="font-bold">{t('cytology.pathologyDepartment')}</p>
            <p className="font-bold text-blue-900">{t('cytology.hospitalName')}</p>
            <p className="text-sm mt-2">{t('cytology.technician')}</p>
          </div>

          <div className="text-center">
            <p className="text-sm italic mb-4">
              {new Date(formData.reportDate).toLocaleString('vi-VN', {
                hour: '2-digit',
                minute: '2-digit',
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </p>
            <p className="font-bold mb-2">{t('cytology.cytopathologist')}</p>
            <input
              type="text"
              value={formData.cytopathologist}
              onChange={(e) => setFormData({ ...formData, cytopathologist: e.target.value })}
              placeholder={t('cytology.enterDoctorName')}
              className="w-full px-3 py-2 border-b-2 border-gray-400 text-center font-bold"
            />
            <div className="mt-8 border-t-2 border-gray-400 pt-2">
              <p className="text-sm text-gray-500">{t('cytology.signArea')}</p>
            </div>
          </div>
        </div>

      </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 mt-8 pt-6 border-t">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CytologyReportTemplate
